import React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import AppHeader from "../components/AppHeader";
import { deleteOwnReview, isReviewOwnedByCurrentUser, listReviewsByCompanySlug, slugifyCompany, updateOwnReview } from "../services/reviews";
import { createNotification } from "../services/notifications";
import { db } from "../firebase";
import { collection, deleteDoc, doc, getDoc, getDocs, limit, orderBy, query, setDoc, where } from "firebase/firestore";
import { getStoredProfileId } from "../utils/profileIdentity";
import RestrictedComment from "../components/RestrictedComment";
import VerificationLevelBadge from "../components/VerificationLevelBadge";
import VerifiedLinkedInBadge from "../components/VerifiedLinkedInBadge";
import {
  resolveUserVerificationDetail,
  resolveEntryVerificationDetail,
  resolveEntryTier,
} from "../utils/verificationLevel";

const ITEM_CONFIG = {
  comunicacao: { label: "Processo de Recrutamento", commentKeys: ["commentComunicacao"] },
  etica: { label: "Proposta salarial e benefícios", commentKeys: ["commentEtica"] },
  cultura: { label: "Visão e valores da empresa", commentKeys: ["commentCultura"] },
  salario: { label: "Data do Pagamento", commentKeys: ["commentSalario", "commentBeneficios"] },
  lideranca: { label: "Acessibilidade e respeito da liderança", commentKeys: ["commentLideranca"] },
  estimacaoOrganizacao: { label: "Condições de trabalho", commentKeys: ["commentEstimacaoOrganizacao"] },
  ambiente: { label: "Estímulo ao respeito", commentKeys: ["commentAmbiente"] },
  diversidade: { label: "Diversidade e Inclusão", commentKeys: ["commentDiversidade"] },
  discriminacao: { label: "Sofreu discriminação?", commentKeys: ["commentDiscriminacao"] },
  cargaHoraria: { label: "Carga Horária / Jornada de Trabalho", commentKeys: ["commentCargaHoraria"] },
  crescimento: { label: "Oportunidades de Desenvolvimento / Crescimento", commentKeys: ["commentCrescimento"] },
  rating: { label: "Segurança e integridade", commentKeys: ["commentRating"] },
  saudeBemEstar: { label: "Preocupação com o bem estar", commentKeys: ["commentSaudeBemEstar"] },
  equilibrio: { label: "Rotatividade", commentKeys: ["commentEquilibrio"] },
  reconhecimento: { label: "Reconhecimento", commentKeys: ["commentReconhecimento"] },
  desenvolvimento: { label: "Planos de cargos e salários", commentKeys: ["commentDesenvolvimento"] },
};

function toDateLabel(value) {
  const parsed = new Date(value || "");
  if (!Number.isFinite(parsed.getTime())) return "";
  return parsed.toLocaleString("pt-BR");
}

function toSortableTime(value) {
  const parsed = new Date(value || "");
  return Number.isFinite(parsed.getTime()) ? parsed.getTime() : 0;
}

function createEmptyReactions() {
  return { thumbsUp: 0, thumbsDown: 0, clap: 0, surprised: 0, cry: 0 };
}

function normalizeReactions(reactions) {
  return {
    thumbsUp: reactions?.thumbsUp || 0,
    thumbsDown: reactions?.thumbsDown || 0,
    clap: reactions?.clap || 0,
    // Compatibilidade com dados antigos que usavam "laugh".
    surprised: reactions?.surprised || reactions?.laugh || 0,
    cry: reactions?.cry || 0,
  };
}

function normalizeReply(reply) {
  return {
    id: reply?.id || `reply_${Date.now()}_${Math.random().toString(16).slice(2)}`,
    author: reply?.author || "Anônimo",
    text: reply?.text || "",
    createdAt:
      typeof reply?.createdAt?.toDate === "function"
        ? reply.createdAt.toDate().toISOString()
        : reply?.createdAt || new Date().toISOString(),
    reactions: normalizeReactions(reply?.reactions),
    replies: Array.isArray(reply?.replies) ? reply.replies.map(normalizeReply) : [],
  };
}

function getTotalReactions(item) {
  return Object.values(item?.reactions || {}).reduce((sum, value) => sum + (value || 0), 0);
}

function normalizeCommentDoc(id, data) {
  return {
    id,
    author: data?.author || "Anônimo",
    text: data?.text || "",
    createdAt:
      typeof data?.createdAt?.toDate === "function"
        ? data.createdAt.toDate().toISOString()
        : data?.createdAt || new Date().toISOString(),
    reactions: normalizeReactions(data?.reactions),
    replies: Array.isArray(data?.replies) ? data.replies.map(normalizeReply) : [],
  };
}

function normalizeEntryThread(entryId, data) {
  return {
    entryId,
    reactions: normalizeReactions(data?.reactions),
    replies: Array.isArray(data?.replies) ? data.replies.map(normalizeReply) : [],
    updatedAt:
      typeof data?.updatedAt?.toDate === "function"
        ? data.updatedAt.toDate().toISOString()
        : data?.updatedAt || new Date().toISOString(),
  };
}

function updateItemById(items, targetId, updater) {
  return (items || []).map((item) => {
    if (item.id === targetId) return updater(item);
    if (!Array.isArray(item.replies) || item.replies.length === 0) return item;
    return {
      ...item,
      replies: updateItemById(item.replies, targetId, updater),
    };
  });
}

function addReplyToItem(items, targetId, reply) {
  return updateItemById(items, targetId, (current) => ({
    ...current,
    replies: [...(current.replies || []), reply],
  }));
}

function applyReactionChangeById(items, targetId, prevReaction, nextReaction) {
  return updateItemById(items, targetId, (current) => {
    const reactions = normalizeReactions(current?.reactions);
    const next = { ...reactions };

    if (prevReaction && next[prevReaction]) {
      next[prevReaction] = Math.max(0, next[prevReaction] - 1);
    }

    if (nextReaction) {
      next[nextReaction] = (next[nextReaction] || 0) + 1;
    }

    return {
      ...current,
      reactions: next,
    };
  });
}

function CompanyItemComments({ theme, toggleTheme }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const companyName = (searchParams.get("name") || "").trim();
  const itemKey = (searchParams.get("item") || "").trim();
  const itemConfig = ITEM_CONFIG[itemKey] || null;

  const [isLoading, setIsLoading] = React.useState(true);
  const [errorMsg, setErrorMsg] = React.useState("");
  const [entries, setEntries] = React.useState([]);
  const [comments, setComments] = React.useState([]);
  const [commentText, setCommentText] = React.useState("");
  const [commentError, setCommentError] = React.useState("");
  const [replyToId, setReplyToId] = React.useState(null);
  const [replyText, setReplyText] = React.useState("");
  const [entryThreads, setEntryThreads] = React.useState({});
  const [entryReplyTarget, setEntryReplyTarget] = React.useState(null);
  const [entryReplyText, setEntryReplyText] = React.useState("");
  const [entryActionNotice, setEntryActionNotice] = React.useState("");
  const [deletingEntryId, setDeletingEntryId] = React.useState("");
  const [editingEntryId, setEditingEntryId] = React.useState(null);
  const [editingComment, setEditingComment] = React.useState("");
  const [editingScore, setEditingScore] = React.useState(0);
  const [savingEditId, setSavingEditId] = React.useState("");
  const [openReactionPickerId, setOpenReactionPickerId] = React.useState(null);
  const [reactionRegistry, setReactionRegistry] = React.useState({});
  const [verificationByProfile, setVerificationByProfile] = React.useState({});
  const reactionHoldTimeout = React.useRef(null);

  const reactions = [
    { key: "thumbsUp", emoji: "👍", label: "Útil" },
    { key: "thumbsDown", emoji: "👎", label: "Não Útil" },
    { key: "clap", emoji: "👏", label: "Concordo" },
    { key: "surprised", emoji: "😮", label: "Surpreso" },
    { key: "cry", emoji: "😢", label: "Triste" },
  ];

  const clearReactionHoldTimer = React.useCallback(() => {
    if (!reactionHoldTimeout.current) return;
    clearTimeout(reactionHoldTimeout.current);
    reactionHoldTimeout.current = null;
  }, []);

  const startReactionHold = React.useCallback(
    (pickerId) => {
      clearReactionHoldTimer();
      reactionHoldTimeout.current = setTimeout(() => {
        setOpenReactionPickerId(pickerId);
        reactionHoldTimeout.current = null;
      }, 450);
    },
    [clearReactionHoldTimer]
  );

  React.useEffect(() => {
    return () => {
      clearReactionHoldTimer();
    };
  }, [clearReactionHoldTimer]);

  const renderHoldToReactControls = ({ pickerId, onReact, getCount }) => (
    <>
      <button
        type="button"
        onMouseDown={() => startReactionHold(pickerId)}
        onMouseUp={clearReactionHoldTimer}
        onMouseLeave={clearReactionHoldTimer}
        onTouchStart={() => startReactionHold(pickerId)}
        onTouchEnd={clearReactionHoldTimer}
        onTouchCancel={clearReactionHoldTimer}
        onClick={(e) => e.preventDefault()}
        className="text-sm px-3 py-1.5 rounded-full border border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-800"
        aria-label="Segure para reagir"
        title="Segure para reagir"
      >
        🙂 Segure para reagir
      </button>

      {openReactionPickerId === pickerId && (
        <div className="flex flex-wrap items-center gap-2">
          {reactions.map((reaction) => (
            <button
              key={`${pickerId}_${reaction.key}`}
              type="button"
              onClick={() => {
                onReact(reaction.key);
                setOpenReactionPickerId(null);
              }}
              className="text-sm px-3 py-1.5 rounded-full border border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-800"
            >
              {reaction.emoji} {getCount(reaction.key)}
            </button>
          ))}
        </div>
      )}
    </>
  );

  const getCommentsStorageKey = React.useCallback(() => {
    if (!companyName || !itemKey) return null;
    return `item_comments_${companyName}_${itemKey}`;
  }, [companyName, itemKey]);

  const getEntryThreadsStorageKey = React.useCallback(() => {
    if (!companyName || !itemKey) return null;
    return `item_entry_threads_${companyName}_${itemKey}`;
  }, [companyName, itemKey]);

  const getReactionRegistryStorageKey = React.useCallback(() => {
    if (!companyName || !itemKey) return null;
    return `item_comment_reactions_${companyName}_${itemKey}`;
  }, [companyName, itemKey]);

  const getCompanySlug = React.useCallback(() => slugifyCompany(companyName || ""), [companyName]);

  const getEntryThreadDocId = React.useCallback((entryId) => {
    const slug = getCompanySlug();
    const safeEntryId = (entryId || "")
      .toString()
      .trim()
      .replace(/[^a-zA-Z0-9_-]+/g, "_");
    return `entry_thread_${slug}_${itemKey}_${safeEntryId}`;
  }, [getCompanySlug, itemKey]);

  const getCurrentPseudonym = React.useCallback(() => {
    return (localStorage.getItem("userPseudonym") || "Anônimo").toString().trim();
  }, []);

  const getStableUserId = React.useCallback(() => {
    try {
      const profileId = getStoredProfileId();
      if (profileId) return `pid:${profileId}`;
    } catch {
      // ignore
    }
    try {
      const profile = JSON.parse(localStorage.getItem("userProfile") || "{}");
      if (profile?.id) return `pid:${profile.id}`;
      if (profile?.uid) return `uid:${profile.uid}`;
      if (profile?.email) return `email:${String(profile.email).toLowerCase()}`;
    } catch {
      // ignore
    }
    const pseudonym = (localStorage.getItem("userPseudonym") || "").toString().trim();
    if (pseudonym) return `pseudo:${pseudonym.toLowerCase()}`;
    return "";
  }, []);

  const getReactionRegistryKey = React.useCallback(
    (targetId) => {
      const userId = getStableUserId();
      if (!targetId || !userId) return "";
      return `${targetId}__${userId}`;
    },
    [getStableUserId]
  );

  const saveComments = React.useCallback(
    (nextComments) => {
      setComments(nextComments);
      try {
        const key = getCommentsStorageKey();
        if (key) localStorage.setItem(key, JSON.stringify(nextComments));
      } catch {
        // ignore
      }
    },
    [getCommentsStorageKey]
  );

  const saveEntryThreads = React.useCallback(
    (nextThreads) => {
      setEntryThreads(nextThreads);
      try {
        const key = getEntryThreadsStorageKey();
        if (key) localStorage.setItem(key, JSON.stringify(nextThreads));
      } catch {
        // ignore
      }
    },
    [getEntryThreadsStorageKey]
  );

  const saveReactionRegistry = React.useCallback(
    (nextRegistry) => {
      setReactionRegistry(nextRegistry);
      try {
        const key = getReactionRegistryStorageKey();
        if (key) localStorage.setItem(key, JSON.stringify(nextRegistry));
      } catch {
        // ignore
      }
    },
    [getReactionRegistryStorageKey]
  );

  const syncCommentsToFirestore = React.useCallback(
    async (nextComments) => {
      const companySlug = slugifyCompany(companyName || "");
      if (!companySlug || !itemKey) return;

      try {
        await Promise.all(
          (nextComments || []).map((comment) =>
            setDoc(
              doc(db, "item_comments", comment.id),
              {
                ...comment,
                companySlug,
                companyName,
                itemKey,
                itemLabel: itemConfig?.label || itemKey,
                updatedAt: new Date().toISOString(),
              },
              { merge: true }
            )
          )
        );
      } catch {
        // ignore remote sync failures
      }
    },
    [companyName, itemConfig?.label, itemKey]
  );

  const syncEntryThreadToFirestore = React.useCallback(
    async (entryId, nextThread) => {
      const companySlug = getCompanySlug();
      if (!companySlug || !itemKey || !entryId) return;

      try {
        await setDoc(
          doc(db, "item_comment_threads", getEntryThreadDocId(entryId)),
          {
            type: "entry_thread",
            companySlug,
            companyName,
            itemKey,
            entryId,
            reactions: normalizeReactions(nextThread?.reactions),
            replies: Array.isArray(nextThread?.replies) ? nextThread.replies : [],
            updatedAt: new Date().toISOString(),
          },
          { merge: true }
        );
      } catch {
        // ignore remote sync failures
      }
    },
    [companyName, getCompanySlug, getEntryThreadDocId, itemKey]
  );

  React.useEffect(() => {
    let alive = true;

    const load = async () => {
      if (!companyName || !itemConfig) {
        setEntries([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setErrorMsg("");

      try {
        const companySlug = slugifyCompany(companyName);
        const reviews = await listReviewsByCompanySlug(companySlug, 300);
        if (!alive) return;

        const commentKeys = Array.isArray(itemConfig.commentKeys) ? itemConfig.commentKeys : [];
        const filtered = (reviews || [])
          .map((review) => {
            // Reúne TODOS os comentários preenchidos deste item (alguns itens
            // agrupam mais de um campo, ex.: salário + benefícios). Antes, o
            // uso de .find() descartava todos menos o primeiro comentário.
            const selectedComment = commentKeys
              .map((key) => review?.[key])
              .filter((value) => typeof value === "string" && value.trim())
              .map((value) => value.trim())
              .join("\n\n");

            if (!selectedComment) return null;

            const restrictedSegments =
              (review?.criterionRestrictedSegments &&
                Array.isArray(review.criterionRestrictedSegments[itemKey])
                ? review.criterionRestrictedSegments[itemKey]
                : []) || [];

            return {
              id: review.id,
              pseudonym: review.pseudonym || "Anônimo",
              authorProfileId: review?.authorProfileId || review?.profileId || "",
              authorLoginProvider: (review?.authorLoginProvider || "").toString().toLowerCase(),
              authorHasLinkedIn: Boolean(review?.authorHasLinkedIn),
              authorHasResume: Boolean(review?.authorHasResume),
              isVerifiedLinkedIn: Boolean(review?.isVerifiedLinkedIn),
              comment: selectedComment.trim(),
              restrictedSegments,
              score: review?.[itemKey],
              createdAt:
                typeof review?.createdAt?.toDate === "function"
                  ? review.createdAt.toDate().toISOString()
                  : review?.createdAt || "",
            };
          })
          .filter(Boolean)
          .sort((a, b) => toSortableTime(b.createdAt) - toSortableTime(a.createdAt));

        setEntries(filtered);
      } catch (err) {
        if (!alive) return;
        setErrorMsg("Não foi possível carregar os comentários deste item.");
      } finally {
        if (!alive) return;
        setIsLoading(false);
      }
    };

    load();

    return () => {
      alive = false;
    };
  }, [companyName, itemConfig, itemKey]);

  // Após carregar os entries, se a URL tiver hash #review-<id>, rola até o
  // comentário e destaca temporariamente (uso vindo do painel admin).
  React.useEffect(() => {
    if (isLoading || !entries || entries.length === 0) return;
    const hash = (typeof window !== "undefined" && window.location.hash) || "";
    if (!hash.startsWith("#review-")) return;
    const id = hash.slice(1);
    const el = document.getElementById(id);
    if (!el) return;
    const timer = setTimeout(() => {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-amber-400", "ring-offset-2");
      setTimeout(() => {
        el.classList.remove("ring-2", "ring-amber-400", "ring-offset-2");
      }, 3000);
    }, 100);
    return () => clearTimeout(timer);
  }, [isLoading, entries]);

  React.useEffect(() => {
    let alive = true;
    // Só buscamos no Firestore os entries que ainda não trazem informação de
    // verificação no próprio registro. Como agora há três níveis, mesmo entries
    // com authorLoginProvider precisam ser checadas para descobrir provider e
    // possível nível "proven". Mantemos o filtro pelo entry só quando o nível
    // mais alto (proven) já está indicado.
    const needLookup = (entries || []).filter(
      (e) => (e?.authorVerificationLevel || "").toString().toLowerCase() !== "proven"
    );
    if (needLookup.length === 0) return;

    const ids = Array.from(
      new Set(needLookup.map((e) => (e?.authorProfileId || "").toString().trim()).filter(Boolean))
    );
    const pseudonyms = Array.from(
      new Set(
        needLookup
          .filter((e) => !((e?.authorProfileId || "").toString().trim()))
          .map((e) => (e?.pseudonym || "").toString().trim())
          .filter((p) => p && p.toLowerCase() !== "anônimo" && p.toLowerCase() !== "anonimo")
      )
    );
    if (ids.length === 0 && pseudonyms.length === 0) return;

    setVerificationByProfile((prev) => {
      const missingIds = ids.filter((id) => !(id in prev));
      const missingPseudos = pseudonyms.filter(
        (p) => !(`pseudonym:${p.toLowerCase()}` in prev)
      );
      if (missingIds.length === 0 && missingPseudos.length === 0) return prev;
      (async () => {
        const idResults = await Promise.all(
          missingIds.map(async (id) => {
            try {
              const snap = await getDoc(doc(db, "users", id));
              if (snap.exists()) return [id, resolveUserVerificationDetail(snap.data(), companyName)];
            } catch {
              // ignore
            }
            return [id, { level: "free", provider: null }];
          })
        );
        const pseudoResults = await Promise.all(
          missingPseudos.map(async (p) => {
            const cacheKey = `pseudonym:${p.toLowerCase()}`;
            try {
              const usersRef = collection(db, "users");
              const variants = [
                query(usersRef, where("pseudonimo", "==", p), limit(1)),
                query(usersRef, where("pseudonym", "==", p), limit(1)),
                query(usersRef, where("name", "==", p), limit(1)),
              ];
              for (const q1 of variants) {
                try {
                  const snap = await getDocs(q1);
                  if (!snap.empty) {
                    return [cacheKey, resolveUserVerificationDetail(snap.docs[0].data(), companyName)];
                  }
                } catch {
                  // ignore
                }
              }
            } catch {
              // ignore
            }
            return [cacheKey, { level: "free", provider: null }];
          })
        );
        if (!alive) return;
        setVerificationByProfile((cur) => {
          const next = { ...cur };
          for (const [k, v] of idResults) if (!(k in next)) next[k] = v;
          for (const [k, v] of pseudoResults) if (!(k in next)) next[k] = v;
          return next;
        });
      })();
      return prev;
    });

    return () => {
      alive = false;
    };
  }, [entries, companyName]);

  React.useEffect(() => {
    const key = getCommentsStorageKey();
    if (!key) {
      setComments([]);
      return;
    }

    try {
      const stored = localStorage.getItem(key);
      setComments(stored ? JSON.parse(stored) : []);
    } catch {
      setComments([]);
    }
  }, [getCommentsStorageKey]);

  React.useEffect(() => {
    const key = getEntryThreadsStorageKey();
    if (!key) {
      setEntryThreads({});
      return;
    }

    try {
      const stored = localStorage.getItem(key);
      setEntryThreads(stored ? JSON.parse(stored) : {});
    } catch {
      setEntryThreads({});
    }
  }, [getEntryThreadsStorageKey]);

  React.useEffect(() => {
    const key = getReactionRegistryStorageKey();
    if (!key) {
      setReactionRegistry({});
      return;
    }

    try {
      const stored = localStorage.getItem(key);
      setReactionRegistry(stored ? JSON.parse(stored) : {});
    } catch {
      setReactionRegistry({});
    }
  }, [getReactionRegistryStorageKey]);

  React.useEffect(() => {
    let alive = true;

    const loadRemoteComments = async () => {
      const companySlug = slugifyCompany(companyName || "");
      if (!companySlug || !itemKey) return;

      try {
        const ref = collection(db, "item_comments");
        const q = query(
          ref,
          where("companySlug", "==", companySlug),
          where("itemKey", "==", itemKey),
          orderBy("createdAt", "desc"),
          limit(120)
        );
        const snap = await getDocs(q);
        if (!alive || snap.empty) return;

        const remoteComments = snap.docs.map((d) => normalizeCommentDoc(d.id, d.data()));
        saveComments(remoteComments);
      } catch {
        // ignore
      }
    };

    loadRemoteComments();

    return () => {
      alive = false;
    };
  }, [companyName, itemKey, saveComments]);

  React.useEffect(() => {
    let alive = true;

    const loadRemoteEntryThreads = async () => {
      const companySlug = getCompanySlug();
      if (!companySlug || !itemKey) return;

      try {
        const ref = collection(db, "item_comment_threads");
        const q = query(ref, where("companySlug", "==", companySlug), limit(300));
        const snap = await getDocs(q);
        if (!alive || snap.empty) return;

        const nextThreads = {};
        snap.docs.forEach((d) => {
          const data = d.data() || {};
          if (data?.type !== "entry_thread") return;
          if (data?.itemKey !== itemKey) return;
          if (!data?.entryId) return;
          nextThreads[data.entryId] = normalizeEntryThread(data.entryId, data);
        });

        if (Object.keys(nextThreads).length > 0) {
          saveEntryThreads(nextThreads);
        }
      } catch {
        // ignore
      }
    };

    loadRemoteEntryThreads();

    return () => {
      alive = false;
    };
  }, [getCompanySlug, itemKey, saveEntryThreads]);

  React.useEffect(() => {
    let cancelled = false;

    const loadRemoteReactions = async () => {
      const companySlug = getCompanySlug();
      const userId = getStableUserId();
      if (!companySlug || !itemKey || !userId) return;

      try {
        const localKey = getReactionRegistryStorageKey();
        let initial = {};
        if (localKey) {
          try {
            const stored = localStorage.getItem(localKey);
            initial = stored ? JSON.parse(stored) : {};
          } catch {
            initial = {};
          }
        }

        const q = query(
          collection(db, "item_comment_reactions"),
          where("userId", "==", userId),
          where("companySlug", "==", companySlug),
          where("itemKey", "==", itemKey)
        );
        const snap = await getDocs(q);
        if (cancelled) return;

        const merged = { ...initial };
        snap.forEach((d) => {
          const data = d.data() || {};
          if (!data?.targetId || !data?.reaction) return;
          merged[`${data.targetId}__${userId}`] = data.reaction;
        });

        saveReactionRegistry(merged);
      } catch {
        // ignore
      }
    };

    loadRemoteReactions();

    return () => {
      cancelled = true;
    };
  }, [getCompanySlug, getStableUserId, getReactionRegistryStorageKey, itemKey, saveReactionRegistry]);

  const handleAddComment = () => {
    const text = (commentText || "").trim();
    if (!text) {
      setCommentError("Digite um comentário para publicar.");
      return;
    }

    const comment = {
      id: `item_comment_${Date.now()}_${Math.random().toString(16).slice(2)}`,
      author: (localStorage.getItem("userPseudonym") || "Anônimo").toString().trim() || "Anônimo",
      text,
      createdAt: new Date().toISOString(),
      reactions: createEmptyReactions(),
      replies: [],
    };

    const next = [comment, ...comments];
    saveComments(next);
    syncCommentsToFirestore(next);
    setCommentText("");
    setCommentError("");
  };

  const handleReply = (targetId) => {
    const text = (replyText || "").trim();
    if (!text) return;

    const reply = {
      id: `item_reply_${Date.now()}_${Math.random().toString(16).slice(2)}`,
      author: (localStorage.getItem("userPseudonym") || "Anônimo").toString().trim() || "Anônimo",
      text,
      createdAt: new Date().toISOString(),
      reactions: createEmptyReactions(),
      replies: [],
    };

    const next = addReplyToItem(comments, targetId, reply);
    saveComments(next);
    syncCommentsToFirestore(next);
    setReplyToId(null);
    setReplyText("");
  };

  const handleReact = (targetId, reactionKey) => {
    const registryKey = getReactionRegistryKey(targetId);
    const prevReaction = registryKey ? reactionRegistry[registryKey] || null : null;
    const nextReaction = prevReaction === reactionKey ? null : reactionKey;

    const next = applyReactionChangeById(comments, targetId, prevReaction, nextReaction);
    saveComments(next);
    syncCommentsToFirestore(next);

    if (registryKey) {
      const nextRegistry = { ...reactionRegistry };
      if (nextReaction) {
        nextRegistry[registryKey] = nextReaction;
      } else {
        delete nextRegistry[registryKey];
      }
      saveReactionRegistry(nextRegistry);
    }

    (async () => {
      try {
        const companySlug = getCompanySlug();
        const userId = getStableUserId();
        if (!companySlug || !itemKey || !targetId || !userId) return;

        const safeUserId = userId.replace(/[^a-zA-Z0-9_:.-]/g, "_");
        const safeTargetId = String(targetId).replace(/[^a-zA-Z0-9_:.-]/g, "_");
        const docId = `${companySlug}__${itemKey}__${safeTargetId}__${safeUserId}`;
        const ref = doc(db, "item_comment_reactions", docId);

        if (nextReaction) {
          await setDoc(
            ref,
            {
              companySlug,
              itemKey,
              targetId,
              userId,
              reaction: nextReaction,
              updatedAt: new Date().toISOString(),
            },
            { merge: true }
          );
        } else {
          await deleteDoc(ref);
        }
      } catch {
        // ignore
      }
    })();
  };

  const getEntryThread = React.useCallback(
    (entryId) =>
      entryThreads?.[entryId] || {
        entryId,
        reactions: createEmptyReactions(),
        replies: [],
        updatedAt: new Date().toISOString(),
      },
    [entryThreads]
  );

  const handleDeleteEntryReview = React.useCallback(
    async (entry) => {
      if (!entry?.id) return;

      const confirmed = window.confirm("Tem certeza que deseja apagar a sua avaliação deste item? Esta ação não pode ser desfeita.");
      if (!confirmed) return;

      setDeletingEntryId(entry.id);
      setEntryActionNotice("");
      setErrorMsg("");

      try {
        await deleteOwnReview({
          reviewId: entry.id,
          currentProfileId: getStoredProfileId(),
          currentPseudonym: getCurrentPseudonym(),
        });

        setEntries((prev) => prev.filter((item) => item.id !== entry.id));

        const nextThreads = { ...entryThreads };
        delete nextThreads[entry.id];
        saveEntryThreads(nextThreads);

        try {
          await deleteDoc(doc(db, "item_comment_threads", getEntryThreadDocId(entry.id)));
        } catch {
          // ignore remote cleanup failures
        }

        try {
          const evaluationsKey = `evaluations_${companyName}`;
          const stored = localStorage.getItem(evaluationsKey);
          if (stored) {
            const parsed = JSON.parse(stored);
            delete parsed[entry.pseudonym];
            localStorage.setItem(evaluationsKey, JSON.stringify(parsed));
          }
        } catch {
          // ignore local cache cleanup failures
        }

        setEntryActionNotice("Sua avaliação foi apagada com sucesso.");
      } catch (err) {
        setErrorMsg(err?.message || "Não foi possível apagar esta avaliação.");
      } finally {
        setDeletingEntryId("");
      }
    },
    [companyName, entryThreads, getCurrentPseudonym, getEntryThreadDocId, saveEntryThreads]
  );

  const handleStartEditEntry = React.useCallback((entry) => {
    setEditingEntryId(entry.id);
    setEditingComment(entry.comment || "");
    setEditingScore(typeof entry.score === "number" ? entry.score : 0);
    setEntryActionNotice("");
    setErrorMsg("");
  }, []);

  const handleSaveEditEntry = React.useCallback(
    async (entry) => {
      if (!entry?.id) return;

      const primaryCommentKey = (itemConfig?.commentKeys || [])[0];
      if (!primaryCommentKey) return;

      setSavingEditId(entry.id);
      setErrorMsg("");

      try {
        await updateOwnReview({
          reviewId: entry.id,
          updates: {
            [primaryCommentKey]: editingComment.trim(),
            [itemKey]: editingScore,
          },
          currentProfileId: getStoredProfileId(),
          currentPseudonym: getCurrentPseudonym(),
        });

        setEntries((prev) =>
          prev.map((item) =>
            item.id !== entry.id
              ? item
              : { ...item, comment: editingComment.trim(), score: editingScore }
          )
        );
        setEditingEntryId(null);
        setEntryActionNotice("Avaliação atualizada com sucesso.");
      } catch (err) {
        setErrorMsg(err?.message || "Não foi possível salvar a edição.");
      } finally {
        setSavingEditId("");
      }
    },
    [editingComment, editingScore, itemConfig, itemKey, getCurrentPseudonym]
  );

  const handleReactEntry = (entryId, targetId, reactionKey) => {
    const currentThread = getEntryThread(entryId);
    const registryKey = getReactionRegistryKey(targetId);
    const prevReaction = registryKey ? reactionRegistry[registryKey] || null : null;
    const nextReaction = prevReaction === reactionKey ? null : reactionKey;
    let nextThread = currentThread;

    if (targetId === entryId) {
      nextThread = {
        ...currentThread,
        reactions: (() => {
          const base = normalizeReactions(currentThread.reactions);
          if (prevReaction && base[prevReaction]) {
            base[prevReaction] = Math.max(0, base[prevReaction] - 1);
          }
          if (nextReaction) {
            base[nextReaction] = (base[nextReaction] || 0) + 1;
          }
          return base;
        })(),
      };
    } else {
      nextThread = {
        ...currentThread,
        replies: applyReactionChangeById(
          currentThread.replies || [],
          targetId,
          prevReaction,
          nextReaction
        ),
      };
    }

    const nextThreads = {
      ...entryThreads,
      [entryId]: {
        ...nextThread,
        updatedAt: new Date().toISOString(),
      },
    };

    saveEntryThreads(nextThreads);
    syncEntryThreadToFirestore(entryId, nextThreads[entryId]);

    if (registryKey) {
      const nextRegistry = { ...reactionRegistry };
      if (nextReaction) {
        nextRegistry[registryKey] = nextReaction;
      } else {
        delete nextRegistry[registryKey];
      }
      saveReactionRegistry(nextRegistry);
    }

    (async () => {
      try {
        const companySlug = getCompanySlug();
        const userId = getStableUserId();
        if (!companySlug || !itemKey || !targetId || !userId) return;

        const safeUserId = userId.replace(/[^a-zA-Z0-9_:.-]/g, "_");
        const safeTargetId = String(targetId).replace(/[^a-zA-Z0-9_:.-]/g, "_");
        const docId = `${companySlug}__${itemKey}__${safeTargetId}__${safeUserId}`;
        const ref = doc(db, "item_comment_reactions", docId);

        if (nextReaction) {
          await setDoc(
            ref,
            {
              companySlug,
              itemKey,
              targetId,
              userId,
              reaction: nextReaction,
              updatedAt: new Date().toISOString(),
            },
            { merge: true }
          );
        } else {
          await deleteDoc(ref);
        }
      } catch {
        // ignore
      }
    })();
  };

  const renderReplies = (items) => {
    return (items || []).map((reply) => (
      <div
        key={reply.id}
        className="mt-3 ml-4 pl-3 border-l-2 border-blue-100 dark:border-slate-700"
      >
        <div className="flex items-start justify-between gap-3">
          <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">{reply.author}</p>
          <p className="text-xs text-slate-500 dark:text-slate-400">{toDateLabel(reply.createdAt)}</p>
        </div>
        <p className="mt-1 text-sm text-slate-700 dark:text-slate-100 whitespace-pre-line">{reply.text}</p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {renderHoldToReactControls({
            pickerId: `discussion_reply_${reply.id}`,
            onReact: (reactionKey) => handleReact(reply.id, reactionKey),
            getCount: (reactionKey) => reply?.reactions?.[reactionKey] || 0,
          })}
          <button
            type="button"
            onClick={() => setReplyToId(reply.id)}
            className="text-xs font-semibold text-blue-700 hover:underline"
          >
            Responder
          </button>
          <span className="text-xs text-slate-500">Total: {getTotalReactions(reply)}</span>
        </div>

        {replyToId === reply.id && (
          <div className="mt-2">
            <textarea
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              rows={2}
              placeholder="Escreva sua resposta..."
              className="w-full p-2 text-sm border border-gray-200 rounded-lg"
            />
            <div className="mt-2 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setReplyToId(null);
                  setReplyText("");
                }}
                className="px-3 py-1 text-xs rounded-lg border border-gray-200"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => handleReply(reply.id)}
                className="px-3 py-1 text-xs rounded-lg bg-blue-600 text-white"
              >
                Enviar resposta
              </button>
            </div>
          </div>
        )}

        {Array.isArray(reply.replies) && reply.replies.length > 0 && renderReplies(reply.replies)}
      </div>
    ));
  };

  const handleReplyToEntryThread = (entryId, targetId) => {
    const text = (entryReplyText || "").trim();
    if (!text) return;

    const reply = {
      id: `item_entry_reply_${Date.now()}_${Math.random().toString(16).slice(2)}`,
      author: (localStorage.getItem("userPseudonym") || "Anônimo").toString().trim() || "Anônimo",
      text,
      createdAt: new Date().toISOString(),
      reactions: createEmptyReactions(),
      replies: [],
    };

    const currentThread = getEntryThread(entryId);
    const nextReplies =
      targetId === entryId
        ? [...(currentThread.replies || []), reply]
        : addReplyToItem(currentThread.replies || [], targetId, reply);

    const nextThread = {
      ...currentThread,
      replies: nextReplies,
      updatedAt: new Date().toISOString(),
    };

    const nextThreads = {
      ...entryThreads,
      [entryId]: nextThread,
    };

    saveEntryThreads(nextThreads);
    syncEntryThreadToFirestore(entryId, nextThread);
    setEntryReplyTarget(null);
    setEntryReplyText("");

    // Notifica o autor da avaliação sobre a resposta ao seu comentário.
    // entryId corresponde ao id da review; buscamos o uid do autor.
    (async () => {
      try {
        const reviewSnap = await getDoc(doc(db, "reviews", entryId));
        if (!reviewSnap.exists()) return;
        const review = reviewSnap.data() || {};
        const authorUid = (review.uid || "").toString().trim();
        if (!authorUid) return;
        const targetCompanyName = review.company || review.companyName || companyName || "";
        await createNotification({
          toUid: authorUid,
          type: "reply",
          reviewId: entryId,
          companySlug: review.companySlug || slugifyCompany(targetCompanyName),
          companyName: targetCompanyName,
          itemKey,
          itemLabel: itemConfig?.label || "",
          message: `${reply.author} respondeu ao seu comentário${targetCompanyName ? ` sobre ${targetCompanyName}` : ""}.`,
          link: `/empresa/comentarios-item?name=${encodeURIComponent(targetCompanyName)}&item=${itemKey}#review-${entryId}`,
        });
      } catch {
        /* best-effort */
      }
    })();
  };

  const renderEntryThreadReplies = (entryId, items) => {
    return (items || []).map((reply) => {
      const targetKey = `${entryId}::${reply.id}`;
      return (
        <div
          key={reply.id}
          className="mt-3 ml-4 pl-3 border-l-2 border-blue-100 dark:border-slate-700"
        >
          <div className="flex items-start justify-between gap-3">
            <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">{reply.author}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">{toDateLabel(reply.createdAt)}</p>
          </div>
          <p className="mt-1 text-sm text-slate-700 dark:text-slate-100 whitespace-pre-line">{reply.text}</p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {renderHoldToReactControls({
              pickerId: `entry_reply_${entryId}_${reply.id}`,
              onReact: (reactionKey) => handleReactEntry(entryId, reply.id, reactionKey),
              getCount: (reactionKey) => reply?.reactions?.[reactionKey] || 0,
            })}
            <button
              type="button"
              onClick={() => setEntryReplyTarget(targetKey)}
              className="text-xs font-semibold text-blue-700 hover:underline"
            >
              Responder
            </button>
            <span className="text-xs text-slate-500">Total: {getTotalReactions(reply)}</span>
          </div>

          {entryReplyTarget === targetKey && (
            <div className="mt-2">
              <textarea
                value={entryReplyText}
                onChange={(e) => setEntryReplyText(e.target.value)}
                rows={2}
                placeholder="Escreva sua resposta..."
                className="w-full p-2 text-sm border border-gray-200 rounded-lg"
              />
              <div className="mt-2 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setEntryReplyTarget(null);
                    setEntryReplyText("");
                  }}
                  className="px-3 py-1 text-xs rounded-lg border border-gray-200"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={() => handleReplyToEntryThread(entryId, reply.id)}
                  className="px-3 py-1 text-xs rounded-lg bg-blue-600 text-white"
                >
                  Enviar resposta
                </button>
              </div>
            </div>
          )}

          {Array.isArray(reply.replies) && reply.replies.length > 0 && renderEntryThreadReplies(entryId, reply.replies)}
        </div>
      );
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 dark:from-slate-950 dark:to-slate-900">
      <AppHeader theme={theme} toggleTheme={toggleTheme} />

      <div className="max-w-4xl mx-auto bg-white dark:bg-slate-800 rounded-3xl shadow-xl border border-blue-100 dark:border-slate-700 p-8 mt-6 mx-4">
        <Link
          to={`/empresa?name=${encodeURIComponent(companyName)}`}
          className="text-sm font-bold text-blue-700 hover:underline"
        >
          {"← Voltar para a página da empresa"}
        </Link>

        <h1 className="mt-4 text-2xl font-extrabold text-blue-800 dark:text-slate-100">
          Comentários por item
        </h1>

        <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
          Empresa: <span className="font-semibold">{companyName || "Não informada"}</span>
        </p>

        <p className="text-sm text-slate-600 dark:text-slate-300">
          Item: <span className="font-semibold">{itemConfig?.label || "Item inválido"}</span>
        </p>

        {isLoading ? (
          <div className="mt-6 text-sm text-slate-600">Carregando comentários...</div>
        ) : errorMsg ? (
          <div className="mt-6 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-3">
            {errorMsg}
          </div>
        ) : entries.length === 0 ? (
          <div className="mt-6 text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-3">
            Ainda não há comentários para este item.
          </div>
        ) : (
          <div className="mt-6 space-y-3">
            {entryActionNotice && (
              <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                {entryActionNotice}
              </div>
            )}
            {entries.map((entry) => (
              <article
                key={entry.id}
                id={`review-${entry.id}`}
                className="bg-white dark:bg-slate-900 p-4 rounded-xl border border-gray-200 dark:border-slate-700 scroll-mt-24 transition-shadow"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    {(() => {
                      const pseudonymRaw = (entry?.pseudonym || "").toString().trim();
                      const isAnon = !pseudonymRaw || pseudonymRaw.toLowerCase() === "anônimo" || pseudonymRaw.toLowerCase() === "anonimo";
                      const pid = (entry?.authorProfileId || "").toString().trim();
                      if (isAnon || !pid) {
                        return <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{entry.pseudonym}</p>;
                      }
                      return (
                        <button
                          type="button"
                          onClick={() => navigate(`/perfil/${encodeURIComponent(pid)}`)}
                          className="text-sm font-semibold text-slate-900 dark:text-slate-100 hover:underline focus:underline cursor-pointer text-left bg-transparent border-0 p-0"
                          title="Ver perfil público do avaliador"
                        >
                          {entry.pseudonym}
                        </button>
                      );
                    })()}
                    {(() => {
                      const detail = resolveEntryVerificationDetail(entry, verificationByProfile, companyName);
                      const tier = resolveEntryTier(entry, verificationByProfile);
                      return (
                        <>
                          <VerificationLevelBadge level={detail.level} provider={detail.provider} />
                          {tier && <VerificationLevelBadge tier={tier} />}
                          {entry.isVerifiedLinkedIn && <VerifiedLinkedInBadge />}
                        </>
                      );
                    })()}
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{toDateLabel(entry.createdAt)}</p>
                </div>
                <RestrictedComment
                  comment={entry.comment}
                  restrictedSegments={entry.restrictedSegments}
                  className="mt-2 text-sm text-slate-700 dark:text-slate-100"
                />
                <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                  Nota neste item: {typeof entry.score === "number" ? entry.score.toFixed(1) : "--"}
                </p>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {renderHoldToReactControls({
                    pickerId: `entry_root_${entry.id}`,
                    onReact: (reactionKey) => handleReactEntry(entry.id, entry.id, reactionKey),
                    getCount: (reactionKey) => getEntryThread(entry.id)?.reactions?.[reactionKey] || 0,
                  })}
                  <button
                    type="button"
                    onClick={() => setEntryReplyTarget(`${entry.id}::${entry.id}`)}
                    className="text-xs font-semibold text-blue-700 hover:underline"
                  >
                    Responder
                  </button>
                  <span className="text-xs text-slate-500">
                    Total: {getTotalReactions({ reactions: getEntryThread(entry.id)?.reactions })}
                  </span>
                  {isReviewOwnedByCurrentUser(entry, { profileId: getStoredProfileId(), pseudonym: getCurrentPseudonym() }) && (
                    <>
                      <button
                        type="button"
                        onClick={() => handleStartEditEntry(entry)}
                        disabled={deletingEntryId === entry.id || savingEditId === entry.id}
                        className="text-xs font-semibold text-indigo-700 hover:underline disabled:opacity-60 disabled:no-underline"
                      >
                        Editar minha avaliação
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteEntryReview(entry)}
                        disabled={deletingEntryId === entry.id || savingEditId === entry.id}
                        className="text-xs font-semibold text-rose-700 hover:underline disabled:opacity-60 disabled:no-underline"
                      >
                        {deletingEntryId === entry.id ? "Apagando avaliação..." : "Apagar minha avaliação"}
                      </button>
                    </>
                  )}

                {editingEntryId === entry.id && (
                  <div className="mt-3 p-3 rounded-xl border border-indigo-200 bg-indigo-50 dark:bg-slate-800 dark:border-slate-600">
                    <p className="text-xs font-semibold text-indigo-700 dark:text-indigo-300 mb-2">Editar avaliação</p>
                    <textarea
                      value={editingComment}
                      onChange={(e) => setEditingComment(e.target.value)}
                      rows={3}
                      maxLength={1000}
                      placeholder="Edite seu comentário..."
                      className="w-full p-2 text-sm border border-indigo-200 rounded-lg dark:bg-slate-700 dark:border-slate-600 dark:text-slate-100"
                    />
                    <div className="mt-2 flex items-center gap-2">
                      <span className="text-xs text-slate-600 dark:text-slate-400">Nota:</span>
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => setEditingScore(star)}
                          className={`text-lg leading-none ${editingScore >= star ? "text-yellow-400" : "text-slate-300 dark:text-slate-600"}`}
                          aria-label={`${star} estrela${star > 1 ? "s" : ""}`}
                        >
                          ★
                        </button>
                      ))}
                      {editingScore > 0 && (
                        <span className="text-xs text-slate-500">{editingScore}/5</span>
                      )}
                    </div>
                    <div className="mt-3 flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setEditingEntryId(null)}
                        disabled={savingEditId === entry.id}
                        className="px-3 py-1 text-xs rounded-lg border border-gray-200 dark:border-slate-600 disabled:opacity-60"
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSaveEditEntry(entry)}
                        disabled={savingEditId === entry.id || !editingComment.trim()}
                        className="px-3 py-1 text-xs rounded-lg bg-indigo-600 text-white disabled:opacity-60"
                      >
                        {savingEditId === entry.id ? "Salvando..." : "Salvar alterações"}
                      </button>
                    </div>
                  </div>
                )}
                </div>

                {entryReplyTarget === `${entry.id}::${entry.id}` && (
                  <div className="mt-3">
                    <textarea
                      value={entryReplyText}
                      onChange={(e) => setEntryReplyText(e.target.value)}
                      rows={2}
                      placeholder="Escreva sua resposta..."
                      className="w-full p-2 text-sm border border-gray-200 rounded-lg"
                    />
                    <div className="mt-2 flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setEntryReplyTarget(null);
                          setEntryReplyText("");
                        }}
                        className="px-3 py-1 text-xs rounded-lg border border-gray-200"
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        onClick={() => handleReplyToEntryThread(entry.id, entry.id)}
                        className="px-3 py-1 text-xs rounded-lg bg-blue-600 text-white"
                      >
                        Enviar resposta
                      </button>
                    </div>
                  </div>
                )}

                {Array.isArray(getEntryThread(entry.id)?.replies) &&
                  getEntryThread(entry.id).replies.length > 0 &&
                  renderEntryThreadReplies(entry.id, getEntryThread(entry.id).replies)}
              </article>
            ))}
          </div>
        )}

        <section className="mt-8 border-t border-blue-100 dark:border-slate-700 pt-6">
          <h2 className="text-lg font-bold text-blue-800 dark:text-slate-100">
            Discussão deste item (comentários, reações e respostas)
          </h2>

          <p className="mt-2 text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
            Você está comentando sobre: <span className="font-semibold">{itemConfig?.label || "Item"}</span>
          </p>

          <div className="mt-3 space-y-2">
            <textarea
              value={commentText}
              onChange={(e) => {
                setCommentText(e.target.value);
                if (commentError) setCommentError("");
              }}
              rows={3}
              placeholder={`Compartilhe sua experiência sobre "${itemConfig?.label || "este item"}"...`}
              className="w-full p-3 text-sm border border-gray-200 rounded-xl"
            />
            {commentError && <p className="text-sm text-rose-700">{commentError}</p>}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleAddComment}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold"
              >
                Publicar comentário
              </button>
            </div>
          </div>

          {comments.length === 0 ? (
            <p className="mt-4 text-sm text-slate-500">Ainda não há comentários nesta discussão.</p>
          ) : (
            <div className="mt-4 space-y-3">
              {[...comments]
                .sort((a, b) => getTotalReactions(b) - getTotalReactions(a))
                .map((comment) => (
                  <article
                    key={comment.id}
                    className="bg-white dark:bg-slate-900 p-4 rounded-xl border border-gray-200 dark:border-slate-700"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{comment.author}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">{toDateLabel(comment.createdAt)}</p>
                    </div>
                    <p className="mt-2 text-sm text-slate-700 dark:text-slate-100 whitespace-pre-line">{comment.text}</p>

                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      {renderHoldToReactControls({
                        pickerId: `discussion_comment_${comment.id}`,
                        onReact: (reactionKey) => handleReact(comment.id, reactionKey),
                        getCount: (reactionKey) => comment?.reactions?.[reactionKey] || 0,
                      })}
                      <button
                        type="button"
                        onClick={() => setReplyToId(comment.id)}
                        className="text-xs font-semibold text-blue-700 hover:underline"
                      >
                        Responder
                      </button>
                      <span className="text-xs text-slate-500">Total: {getTotalReactions(comment)}</span>
                    </div>

                    {replyToId === comment.id && (
                      <div className="mt-3">
                        <textarea
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          rows={2}
                          placeholder="Escreva sua resposta..."
                          className="w-full p-2 text-sm border border-gray-200 rounded-lg"
                        />
                        <div className="mt-2 flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              setReplyToId(null);
                              setReplyText("");
                            }}
                            className="px-3 py-1 text-xs rounded-lg border border-gray-200"
                          >
                            Cancelar
                          </button>
                          <button
                            type="button"
                            onClick={() => handleReply(comment.id)}
                            className="px-3 py-1 text-xs rounded-lg bg-blue-600 text-white"
                          >
                            Enviar resposta
                          </button>
                        </div>
                      </div>
                    )}

                    {Array.isArray(comment.replies) && comment.replies.length > 0 && renderReplies(comment.replies)}
                  </article>
                ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

export default CompanyItemComments;
